<script setup>
import { ref, onMounted, nextTick } from "vue";
import { DataTable } from "simple-datatables";
import { useStore } from "vuex";
import { TilopayClient } from "@/services/TiloPayClient";

const store = useStore();
const tilopayClient = new TilopayClient();

const headers = ["N° Orden", "Usuario", "Curso", "Monto", "Estado", "Descripción", "Fecha"];
const payments = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(50);
const totalItems = ref(0);

const fetchPayments = async () => {
  loading.value = true;
  try {
    const response = await tilopayClient.get(`/history?page=${currentPage.value}&limit=${itemsPerPage.value}`);
    
    if (response && response.data) {
      payments.value = response.data.map(payment => ({
        orderNumber: payment.order_number || payment.id,
        user: payment.user_name || 'N/A',
        course: payment.course_title || 'N/A',
        amount: formatAmount(payment.amount, payment.currency),
        rawStatus: payment.status, 
        status: getStatusText(payment.status), 
        date: formatDate(payment.created_at),
        formattedDate: formatFullDate(payment.created_at),
        description: payment.description || 'Transacción de pago'
      }));
      
      totalItems.value = response.total || response.data.length;
    }
  } catch (err) {
    console.error("Error cargando pagos:", err);
  } finally {
    loading.value = false;
  }
};

const formatAmount = (amount, currency) => {
  if (!amount) return 'N/A';
  const formattedAmount = parseFloat(amount).toFixed(2);
  
  const currencySymbol = currency === 'USD' ? '$' : 
                        currency === 'CRC' ? '₡' : 
                        currency || '';
  
  if (currency === 'CRC') {
    return `${currencySymbol} ${formattedAmount}`;
  } else {
    return `${currencySymbol}${formattedAmount}`;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFullDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', { 
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const getStatusText = (status) => {
  const statusMap = {
    'approved': 'Aprobado',
    'pending': 'Pendiente',
    'failed': 'Fallido',
    'completed': 'Completado',
    'refunded': 'Reembolsado'
  };
  return statusMap[status.toLowerCase()] || status;
};

const getStatusClass = (status) => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'badge-success text-white';
    case 'pending':
      return 'badge-secondary';
    case 'failed':
      return 'badge-danger text-white';
    case 'refunded':
      return 'badge-info text-white';
    default:
      return 'badge-warning text-dark';
  }
};

onMounted(async () => {
  await fetchPayments();
  await nextTick();

  const tableElement = document.getElementById("payments-table");
  if (tableElement) {
    new DataTable(tableElement, {
      searchable: true,
      fixedHeight: true,
      perPage: 5,
      labels: {
        placeholder: "Buscar pagos...",
        perPage: "{select} registros por página",
        noRows: "No se encontraron pagos",
        info: "Mostrando {start} a {end} de {rows} registros",
      },
    });
  }
});
</script>

<template>
  <div class="card">
    <div class="p-3 pb-0 card-header">
      <div class="d-flex justify-content-between">
        <h6 class="mb-2"><i class="fas fa-credit-card pe-2"></i>Historial de Pagos</h6>
        <div v-if="loading" class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
    </div>

    <div class="table-responsive pb-4 ">
      <table id="payments-table" class="table align-items-center mb-0 px-4">
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index"
              class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 px-2 py-3">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody >
          <tr v-for="payment in payments" :key="payment.orderNumber"  
              class="row-hover"
              @mouseenter="handleMouseEnter(payment, $event)"
              @mouseleave="handleMouseLeave">
            <td class="px-2 py-3 text-xs">{{ payment.orderNumber }}</td>
            <td class="px-2 py-3 text-xs">{{ payment.user }}</td>
            <td class="px-2 py-3 text-xs text-wrap">{{ payment.course }}</td>
            <td class="px-2 py-3 text-xs">{{ payment.amount }}</td>
            <td class="px-2 py-3">
              <span :class="['badge', 'badge-sm', getStatusClass(payment.rawStatus)]">
                {{ payment.status }}
              </span>
            </td>
            <td class="px-2 py-3 text-xs text-wrap">{{ payment.description }}</td>
         
            <td class="px-2 py-3 text-xs text-wrap">{{ payment.date }}</td>
          </tr>
          <tr v-if="payments.length === 0 && !loading">
            <td colspan="7" class="text-center py-4">No se encontraron registros de pagos</td>
          </tr>
        </tbody>
      </table>
      
    </div>
  
  </div>
</template>

<style scoped>
.row-hover {
  transition: background-color 0.3s ease;
  position: relative;
}

.row-hover:hover {
  background-color: #f0f4f8;
}

.badge-success {
  background-color: #28a745 !important;
}

.badge-secondary {
  background-color: #6c757d !important;
  color: white !important;
}

.badge-danger {
  background-color: #dc3545 !important;
}

.dataTable-input,
::v-deep(.dataTable-input) {
  width: 500px !important;
  max-width: 100%;
}

.text-xxs {
  font-size: 0.7rem !important;
}


.cell-content {
  min-width: 100%;
  display: inline-block;
}

/* Ensure the table itself doesn't overflow the card */
.table-responsive {
  overflow-x: auto;
}

</style>